import { useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  EyeOff,
  Target,
  Glasses,
  Focus,
  Eraser,
  Eye,
  Copy,
  ExternalLink,
  ArrowLeftRight,
  Compass,
  Layers,
  Building2,
  Tag,
} from 'lucide-react'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { findGuid } from '@viewer/lib/ifcItemData'

// ---------------------------------------------------------------------------
// Right-click context menu over the 3D canvas. The menu reads its
// position + target from the selection slice (openContextMenu sets
// them, closeContextMenu wipes). Closes on Esc, on outside click, or
// after any item action runs.
//
// We branch the action set in two:
//   • selection-aware actions (Esconder / Isolar / Tornar transparente
//     / Focar / Inverter seleção / Copiar GUID / Inspeccionar) only
//     show when there's at least one selected element
//   • cleanup actions (Mostrar tudo / Repor opacidade / Limpar
//     seleção) only show when there's something to revert
// ---------------------------------------------------------------------------

const MENU_W = 300

function ContextMenu() {
  const ctx = useAppStore((s) => s.contextMenu)
  const close = useAppStore((s) => s.closeContextMenu)
  const targets = useAppStore((s) => s.selectedTargets)
  const selection = useAppStore((s) => s.selection)
  const hiddenCount = useAppStore((s) => s.hiddenCount)
  const transparentCount = useAppStore((s) => s.transparentCount)
  const clearSelection = useAppStore((s) => s.clearSelection)
  const openPopup = useAppStore((s) => s.openPopup)
  const ref = useRef<HTMLDivElement | null>(null)

  // Outside-click + Escape: dismiss the menu without any action.
  useEffect(() => {
    if (!ctx) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        close()
      }
    }
    const onPointerDown = (e: PointerEvent) => {
      const node = ref.current
      if (!node) return
      if (node.contains(e.target as Node)) return
      close()
    }
    window.addEventListener('keydown', onKey, { capture: true })
    window.addEventListener('pointerdown', onPointerDown, { capture: true })
    return () => {
      window.removeEventListener('keydown', onKey, { capture: true })
      window.removeEventListener('pointerdown', onPointerDown, {
        capture: true,
      })
    }
  }, [ctx, close])

  // Clamp the menu inside the viewport. Computed once per open.
  const placement = useMemo(() => {
    if (!ctx) return { x: 0, y: 0 }
    const w = MENU_W
    const margin = 8
    const x = Math.min(ctx.x, window.innerWidth - w - margin)
    const y = Math.min(ctx.y, window.innerHeight - 320)
    return { x: Math.max(margin, x), y: Math.max(margin, y) }
  }, [ctx])

  if (!ctx) return null

  const hasSelection = targets.length > 0
  const single = selection
  const run = (fn: () => unknown | Promise<unknown>) => {
    void Promise.resolve(fn()).finally(() => close())
  }

  const items: Array<
    | {
        type: 'item'
        icon: typeof EyeOff
        label: string
        shortcut?: string
        onClick: () => void
        disabled?: boolean
      }
    | { type: 'sep' }
  > = []

  if (hasSelection) {
    items.push({
      type: 'item',
      icon: EyeOff,
      label: 'Esconder',
      shortcut: 'H',
      onClick: () => run(() => viewerApi.hideSelected()),
    })
    items.push({
      type: 'item',
      icon: Target,
      label: 'Isolar',
      shortcut: 'I',
      onClick: () => run(() => viewerApi.isolateSelected()),
    })
    items.push({
      type: 'item',
      icon: Glasses,
      label: 'Tornar transparente',
      onClick: () => run(() => viewerApi.makeTransparent()),
    })
    items.push({
      type: 'item',
      icon: Focus,
      label: 'Focar',
      shortcut: 'F',
      onClick: () => run(() => viewerApi.focusOnSelection()),
    })
    items.push({
      type: 'item',
      icon: Compass,
      label: 'Definir eixo de rotação aqui',
      shortcut: 'P',
      onClick: () =>
        run(() => {
          if (ctx.target) {
            return viewerApi.setOrbitAndLookAtElement(
              ctx.target.modelId,
              ctx.target.localId,
            )
          }
          return viewerApi.setOrbitAndLookAtSelection()
        }),
    })
    items.push({
      type: 'item',
      icon: ArrowLeftRight,
      label: 'Inverter seleção',
      shortcut: 'Shift+I',
      onClick: () => run(() => viewerApi.invertSelection()),
    })
    if (single) {
      items.push({ type: 'sep' })
      items.push({
        type: 'item',
        icon: Tag,
        label: 'Selecionar instâncias da classe',
        shortcut: 'Shift+C',
        onClick: () =>
          run(async () => {
            const r = await viewerApi.selectAllOfClass(
              single.modelId,
              single.localId,
            )
            if (r) {
              console.debug(
                `[ContextMenu] selecionados ${r.count} ${r.category}`,
              )
            }
          }),
      })
      items.push({
        type: 'item',
        icon: Tag,
        label: 'Selecionar instâncias do tipo',
        shortcut: 'Shift+T',
        onClick: () =>
          run(async () => {
            const r = await viewerApi.selectAllOfSameType(
              single.modelId,
              single.localId,
            )
            if (!r || r.count === 0) {
              window.alert(
                'Este elemento não tem IfcType associado (modelo instance-only).',
              )
            }
          }),
      })
      items.push({
        type: 'item',
        icon: Layers,
        label: 'Selecionar tudo no contentor (parent)',
        onClick: () =>
          run(() =>
            viewerApi.selectAllInSpatialContainer(
              single.modelId,
              single.localId,
              'parent',
            ),
          ),
      })
      items.push({
        type: 'item',
        icon: Layers,
        label: 'Selecionar conteúdo deste elemento',
        onClick: () =>
          run(() =>
            viewerApi.selectAllInSpatialContainer(
              single.modelId,
              single.localId,
              'self',
            ),
          ),
      })
      items.push({
        type: 'item',
        icon: Layers,
        label: 'Selecionar tudo no piso (storey)',
        shortcut: 'Shift+S',
        onClick: () =>
          run(() =>
            viewerApi.selectAllInSpatialContainer(
              single.modelId,
              single.localId,
              'storey',
            ),
          ),
      })
      items.push({
        type: 'item',
        icon: Building2,
        label: 'Selecionar tudo no edifício',
        shortcut: 'Shift+B',
        onClick: () =>
          run(() =>
            viewerApi.selectAllInSpatialContainer(
              single.modelId,
              single.localId,
              'building',
            ),
          ),
      })
    }
    items.push({ type: 'sep' })
    items.push({
      type: 'item',
      icon: Copy,
      label: 'Copiar GlobalId',
      disabled: !single,
      onClick: () =>
        run(async () => {
          if (!single) return
          const m = viewerApi.getModel(single.modelId)
          if (!m) return
          const data = await m
            .getItemsData([single.localId], { attributesDefault: true })
            .catch(() => [])
          const first = data?.[0]
          if (!first || typeof first !== 'object') return
          const guid = findGuid(first as Record<string, unknown>)
          if (guid) await navigator.clipboard?.writeText(guid)
        }),
    })
    items.push({
      type: 'item',
      icon: ExternalLink,
      label: 'Inspeccionar em janela',
      disabled: !single,
      onClick: () =>
        run(() => {
          if (single) openPopup(single)
        }),
    })
    items.push({
      type: 'item',
      icon: Eraser,
      label: 'Limpar seleção',
      shortcut: 'Esc',
      onClick: () => run(() => clearSelection()),
    })
  }

  if (hiddenCount > 0 || transparentCount > 0) {
    if (items.length > 0) items.push({ type: 'sep' })
    if (hiddenCount > 0) {
      items.push({
        type: 'item',
        icon: Eye,
        label: `Mostrar tudo (${hiddenCount})`,
        shortcut: 'Alt+H',
        onClick: () => run(() => viewerApi.showAll()),
      })
    }
    if (transparentCount > 0) {
      items.push({
        type: 'item',
        icon: Eye,
        label: `Repor opacidade (${transparentCount})`,
        onClick: () => run(() => viewerApi.clearTransparent()),
      })
    }
  }

  if (items.length === 0) {
    items.push({
      type: 'item',
      icon: Eraser,
      label: 'Nada para mostrar',
      onClick: close,
      disabled: true,
    })
  }

  // Vai para o body: a vista 3D isola as suas camadas (`isolate`) e um
  // menu lá dentro ficava por baixo das janelas da página. Banda dos
  // menus, ver core/layout/camadas.ts.
  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label="Menu contextual"
      className="fixed z-[900] rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] py-1 text-xs shadow-2xl"
      style={{
        left: placement.x,
        top: placement.y,
        width: MENU_W,
      }}
    >
      {items.map((it, i) =>
        it.type === 'sep' ? (
          <div
            key={`sep-${i}`}
            className="my-1 h-px bg-[var(--color-border)]/60"
          />
        ) : (
          <button
            key={`item-${i}-${it.label}`}
            type="button"
            role="menuitem"
            disabled={it.disabled}
            onClick={it.onClick}
            className={`flex w-full items-center gap-2 px-2 py-1 text-left transition-colors ${
              it.disabled
                ? 'cursor-not-allowed text-[var(--color-text-dim)] opacity-60'
                : 'text-[var(--color-text)] hover:bg-[var(--color-bg)]'
            }`}
          >
            <it.icon
              size={14}
              strokeWidth={1.6}
              className="shrink-0 text-[var(--color-text-muted)]"
            />
            <span className="flex-1 truncate">{it.label}</span>
            {it.shortcut && (
              <code className="shrink-0 rounded bg-[var(--color-bg)]/60 px-1 py-0 font-mono text-[10px] text-[var(--color-text-muted)]">
                {it.shortcut}
              </code>
            )}
          </button>
        ),
      )}
    </div>,
    document.body,
  )
}

export default ContextMenu
